const View = require('public_framework/view')
const EventsListState = require('../models/events_list_state_v2')
const dateFnsFormat = require('date-fns/format')

const EXTRA_TOP_OFFSET = 60

/**
  Top-level component representing events list page.
  Manages multiple events for:
    - Events list section
    - Location search field
*/

const windowLocation = window.location
const PARAM_KEYS = [
  'location',
  'latitude',
  'longitude',
  'keywords',
  'start_date_from',
  'start_date_to',
  'category',
  'types[]',
  'skills[]'
]

module.exports = class EventsList extends View {
  get events () {
    return {
      'input @search_field': 'syncSearchField',
      'keywords_selected @keywords_search_field': 'handleSearch',
      'location_selected @search_field': 'handleSearch',
      'click @search_button': 'handleSearch',
      'click @link': 'handleLinkClick',
      'click @reset_link': 'handleResetClick',
      'click @toggle_fields': 'handleToggleFieldsClick',
      'click @clear_fields': 'handleClearFieldsClick',
      'click @clear_date': 'handleClearDateClick',
      'click @reset_keywords': 'handleResetKeywords',
      'click @reset_location': 'handleResetLocation',
      'keyup @keywords_search_field': 'handleKeywordsKeyUp',
      'keyup @search_field': 'handleLocationKeyUp',
      'click @clear_current_field': 'handleClearCurrentFieldClick',
      'click @clear_all_fields': 'resetAllFilters'
    }
  }

  get els () {
    return {
      $keywordsField: '@keywords_search_field',
      $locationField: '@search_field',
      $dateField: '@date_field',
      $categoryField: '@category_field:checked',
      $typeField: '@type_field:checked',
      $skillField: '@skill_field:checked',
      $resetKeywords: '@reset_keywords',
      $resetLocation: '@reset_location',
      $searchContainer: '@search_container'
    }
  }

  constructor () {
    super(...arguments)

    this.state = new EventsListState({
      url: windowLocation.href,
      content: this.$('@events_container').html(),
      locations: this.$('@events_container [data-locations]').data('locations'),
      title: document.title,
      description: this.getDocumentMetaDescriptionContent(),
      ogDescription: this.getDocumentMetaOgDescriptionContent(),
      prevLink: this.getDocumentMetaLink('prev'),
      nextLink: this.getDocumentMetaLink('next'),
      canonicalLink: this.getDocumentMetaLink('canonical'),
      categoriesFilterLabels: this.getFilterLabels('category'),
      typesFilterLabels: this.getFilterLabels('type'),
      skillsFilterLabels: this.getFilterLabels('skill')
    })

    this.subscribeToState()
    this.handleSearchOptionsChange()
    this.addCommonSearchClickListeners()
    this.updateClearButtons()
    this.updateFieldLabels()
  }

  handleKeywordsKeyUp (e) {
    const shouldReset = this.$keywordsField()[0].value.length !== 0
    if (shouldReset) {
      this.$resetKeywords().addClass('enabled')
    } else {
      this.$resetKeywords().removeClass('enabled')
    }

    if (e.keyCode === 13) {
      this.handleSearch()
    }
  }

  handleLocationKeyUp (e) {
    const shouldReset = this.$locationField()[0].value.length !== 0
    if (shouldReset) {
      this.$resetLocation().addClass('enabled')
    } else {
      this.$resetLocation().removeClass('enabled')
    }
    if (e.keyCode === 13) {
      this.handleSearch()
    }
  }

  subscribeToState () {
    this.state.on('change:loading', (_state, loading) => {
      this.$el.toggleClass('is-loading', loading)
    })

    this.state.on('change:content', (_state, content) => {
      this.$html(this.$('@events_container'), content)
    })

    this.state.on('change:page', (_state, page) => {
      const { top } = this.$el.offset()
      if (top < window.scrollY) window.scrollTo(0, top - EXTRA_TOP_OFFSET)
    })

    this.state.on('change:radius', (_state, radius) => {
      this.$el.toggleClass('has-radius', !!radius)
    })

    this.state.on('change:location', (state, location) => {
      this.$el.toggleClass('has-location', !!location)
      this.$('@search_field').val(location)
    })

    this.state.on('change:title', (state, title) => {
      document.title = title
    })

    this.state.on('change:description', (state, description) => {
      this.setDocumentMetaDescriptionContent(description)
    })

    this.state.on('change:ogDescription', (state, ogDescription) => {
      this.setDocumentMetaOgDescriptionContent(ogDescription)
    })

    this.state.on('change:prevLink', (state, prevLink) => {
      this.setDocumentMetaLink('prev', prevLink)
    })

    this.state.on('change:nextLink', (state, nextLink) => {
      this.setDocumentMetaLink('next', nextLink)
    })

    this.state.on('change:canonicalLink', (state, canonicalLink) => {
      this.setDocumentMetaLink('canonical', canonicalLink)
    })

    this.state.on('change:categoriesFilterLabels', (state, labels) => {
      this.setFilterLabels('type', labels)
    })

    this.state.on('change:typesFilterLabels', (state, labels) => {
      this.setFilterLabels('category', labels)
    })

    this.state.on('change:skillsFilterLabels', (state, labels) => {
      this.setFilterLabels('skill', labels)
    })
  }

  unload () {
    this.state.destroy()
  }

  handleLinkClick (e) {
    e.preventDefault()
    this.state.requestEvents(e.currentTarget.href)
  }

  handleResetKeywords () {
    const keyword = this.$keywordsField().val()
    this.$keywordsField().val('')
    this.$resetKeywords().removeClass('enabled')
    this.resetMatchingSkill(keyword)
    this.handleSearch()
  }

  handleResetLocation () {
    this.$locationField().val('')
    this.$resetLocation().removeClass('enabled')
    this.handleSearch()
  }

  resetMatchingSkill (keyword) {
    const skillCheckbox = document.getElementById(`skill-item=${this.toSnakeCase(keyword)}`)

    if (skillCheckbox) {
      skillCheckbox.checked = false
    }

    this.updateFieldLabels()
  }

  handleSearch () {
    const finalUrl = new URLSearchParams(window.location.search)
    const location = this.$locationField().val()

    PARAM_KEYS.forEach((key) => {
      finalUrl.delete(key)
    })

    if (location) {
      this.composeWithLocation(location, finalUrl)
      return
    }

    this.composeWithoutLocation(finalUrl)
    this.hideModalSearch()
    this.mapKeywordsToSkills()
    this.updateFieldLabels()
    this.updateClearButtons()
  }

  composeWithLocation (location, url) {
    this.state.requestEventsForCity(location).then((locationData) => {
      if (locationData) {
        url.set('location', locationData.location)
        url.set('latitude', locationData.latitude)
        url.set('longitude', locationData.longitude)
      }
      this.composeWithoutLocation(url)
    })
  }

  composeWithoutLocation (url) {
    const date = this.$dateField().val()
    const category = this.$categoryField().val()
    const keywords = this.$keywordsField().val()

    if (keywords) {
      url.set('keywords', keywords)
    }

    if (date) {
      const [from, to] = date.split('to')
      const startDateFrom = from ? new Date(from.trim()) : null
      const startDateTo = to ? new Date(to.trim()) : null
      if (startDateFrom) {
        url.set('start_date_from', dateFnsFormat(startDateFrom, 'DD-MM-YYYY'))
      }
      if (startDateTo) {
        url.set('start_date_to', dateFnsFormat(startDateTo, 'DD-MM-YYYY'))
      }
    }

    if (category) {
      url.set('category', category)
    }

    this.$typeField().each((idx) => {
      url.append('types[]', this.$typeField()[idx].value)
    })

    this.$skillField().each((idx) => {
      url.append('skills[]', this.$skillField()[idx].value)
    })

    if (url.has('page')) {
      url.delete('page')
    }

    this.hideToggleFieldOptions()
    this.state.requestEvents(`events?${decodeURI(url.toString())}`)
  }

  hideModalSearch () {
    const classToRemove = this.$searchContainer().data('active-class')
    this.$searchContainer().removeClass(classToRemove)
  }

  handleResetClick (e) {
    e.preventDefault()
    this.state.requestAllEvents()
    this.resetAllFilters()
  }

  handleToggleFieldsClick (e) {
    e.stopPropagation()
    const currentEl = this.$(e.currentTarget)
    const isOpen = this.$(e.currentTarget).parent().hasClass('is-open')

    this.hideToggleFieldOptions()

    if (!isOpen) {
      currentEl.parent().addClass('is-open')
    }
  }

  resetAllFilters () {
    this.emit('datepicker:clear')

    const options = document.getElementsByClassName('expandable_field-option')
    Object.values(options).forEach((element) => {
      element.getElementsByTagName('input')[0].checked = false
    })

    this.updateClearButtons()
  }

  hideToggleFieldOptions () {
    this.$('@toggle_fields').parent().removeClass('is-open')
  }

  handleClearCurrentFieldClick (e) {
    e.stopPropagation()
    const inputs = this.$(e.currentTarget)
      .closest('.expandable_field-options')
      .find('input')
    inputs.prop('checked', false)
    this.clearMatchingKeywordField(inputs)
    this.updateClearButtons(inputs)
  }

  clearMatchingKeywordField (inputs) {
    Object.values(inputs).forEach(input => {
      const keyword = this.$keywordsField()[0].value
      if (this.toSnakeCase(keyword) === input.value) {
        this.handleResetKeywords()
      }
    })
  }

  handleClearDateClick (e) {
    e.stopPropagation()
    this.emit('datepicker:clear')
    this.updateClearButtons()
  }

  updateFieldLabels () {
    this.$('.expandable_field-options').each((i, el) => {
      const currentEl = this.$(el)
      const type = currentEl.data('type')

      let input = ''
      let value = ''

      switch (type) {
        case 'radio':
          input = currentEl.find('input:checked')
          value = input.data('label')
          break
        case 'date':
          input = this.$('input#search_form_date')
          const formatDate = (date) => date.trim().substr(0, 6)
          const [startDate, endDate] = input.val().split('to')
          value = endDate
            ? `${formatDate(startDate)} - ${formatDate(endDate)}`
            : formatDate(startDate)
          break
        case 'checkbox':
          input = currentEl.find('input:checked').eq(0)
          const name = input.prop('name')
          const selectedItems = this.$(`input:checkbox[name="${name}"]:checked`)

          value = input
            .closest('.expandable_field')
            .find('.expandable_field-toggle_options')
            .data('placeholder')

          if (selectedItems.length > 1) {
            value += ` &bull; ${selectedItems.length}`
          } else if (selectedItems.length === 1) {
            value = selectedItems.data('label')
          }
          break
      }

      const targetLabel = currentEl
        .parent()
        .find('.expandable_field-selected_value.is-desktop-only')
      const placeholder = currentEl
        .parent()
        .find('.expandable_field-toggle_options')
        .data('placeholder')
      targetLabel.html(value || placeholder)
    })
  }

  handleSearchOptionsChange () {
    this.$('.expandable_field-options input').on('change', () => {
      this.updateClearButtons()
    })
  }

  updateClearButtons () {
    let hasAnyValue = false

    this.$('.expandable_field-options').each((i, el) => {
      const currentEl = this.$(el)
      const type = currentEl.data('type')

      let input = ''

      switch (type) {
        case 'radio':
          input = currentEl.find('input:checked')
          break
        case 'date':
          input = this.$('input#search_form_date')
          break
        case 'checkbox':
          input = currentEl.find('input:checked').eq(0)
          break
      }

      const hasValue = input.val()
      currentEl.find('.button_clear').attr('disabled', !hasValue)
      if (hasValue) {
        hasAnyValue = true
      }
    })

    this.$('.expandable_field-footer--mobile .button_clear').attr(
      'disabled',
      !hasAnyValue
    )
  }

  addCommonSearchClickListeners () {
    document.addEventListener('click', () => {
      this.hideToggleFieldOptions()
    })
    this.$('.expandable_field-options').on('click', (e) => {
      if (!this.$(e.target).hasClass('button')) {
        e.stopPropagation()
      }
    })
  }

  getDocumentMetaDescriptionContent () {
    const descriptionNode = document.querySelector('meta[name="description"]')
    if (descriptionNode) {
      return descriptionNode.getAttribute('content')
    }
    return ''
  }

  setDocumentMetaDescriptionContent (description) {
    const descriptionNode = document.querySelector('meta[name="description"]')
    if (descriptionNode) {
      descriptionNode.setAttribute('content', description)
    }
  }

  getDocumentMetaOgDescriptionContent () {
    const ogDescriptionNode = document.querySelector(
      'meta[property="og:description"]'
    )
    if (ogDescriptionNode) {
      return ogDescriptionNode.getAttribute('content')
    }
    return ''
  }

  setDocumentMetaOgDescriptionContent (ogDescription) {
    const ogDescriptionNode = document.querySelector(
      'meta[property="og:description"]'
    )
    if (ogDescriptionNode) {
      ogDescriptionNode.setAttribute('content', ogDescription)
    }
  }

  getDocumentMetaLink (rel) {
    const node = document.querySelector('link[rel="' + rel + '"]')
    if (node) {
      return node.getAttribute('href')
    }
    return ''
  }

  setDocumentMetaLink (rel, link) {
    let node = document.querySelector('link[rel="' + rel + '"]')
    if (node && link) {
      node.setAttribute('href', link)
    } else if (node) {
      node.remove()
    } else if (link) {
      node = document.createElement('link')
      node.setAttribute('rel', rel)
      node.setAttribute('href', link)
      document.head.appendChild(node)
    }
  }

  setFilterLabels (name, labels) {
    this.$(`[id^="${name}-item"]`).each((i, el) => {
      const found = labels.find((label) => label.value === el.value)
      this.$(el.labels[0])
        .find('.expandable_field-option_name')
        .html(`${found.label} (${found.total_count})`)
    })
  }

  getFilterLabels (name) {
    const filterLabels = this.$(`[id^="${name}-item"]`).map((i, el) => {
      return {
        label: el.getAttribute('data-label'),
        totalCount: parseInt(el.getAttribute('data-total-count'))
      }
    })
    return filterLabels
  }

  toSnakeCase (str) {
    return str && str
      .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
      .map(x => x.toLowerCase())
      .join('_')
  }

  mapKeywordsToSkills () {
    const keywords = this.$keywordsField().val()
    const skillCheckbox = document.getElementById(`skill-item=${this.toSnakeCase(keywords)}`)

    if (skillCheckbox) {
      skillCheckbox.checked = 'true'
    }
  }
}



// WEBPACK FOOTER //
// ./front/apps/public/pages/community/javascripts/views/events_list.js